import { useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { KeyRound, ClipboardEdit, PhoneCall } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatSlot, sessionStatusLabel, sessionStatusStyle, caseStatusLabel } from "@/lib/caseLog";
import CaseLogDialog from "@/components/technician/CaseLogDialog";
import ChangePasswordDialog from "@/components/technician/ChangePasswordDialog";

function SessionCard({ s, onEdit }) {
  const { date, slot } = formatSlot(s.scheduled_at);
  return (
    <div data-testid={`tech-session-${s.id}`} className="soft-card p-5 flex flex-col gap-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-display text-lg font-bold text-slate-900" data-testid={`tech-session-customer-${s.id}`}>{s.user_name}</div>
          <div className="text-sm text-slate-600">{s.category}</div>
        </div>
        <span data-testid={`tech-session-status-${s.id}`} className={`text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-md border shrink-0 ${sessionStatusStyle[s.status] || ""}`}>
          {sessionStatusLabel[s.status] || s.status}
        </span>
      </div>
      <div className="text-sm text-slate-700 grid grid-cols-1 sm:grid-cols-2 gap-1">
        <div><span className="text-slate-500">Date:</span> <b>{date}</b></div>
        <div><span className="text-slate-500">Time slot:</span> <b>{slot}</b></div>
        {s.phone && <div className="flex items-center gap-1"><PhoneCall className="w-3.5 h-3.5" /> {s.phone}</div>}
      </div>
      {s.case_log && (
        <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg p-2">
          Last log: {caseStatusLabel[s.case_log.status]} · {s.case_log.duration_minutes} min · {new Date(s.case_log.updated_at).toLocaleString("en-IN")}
        </div>
      )}
      <Button data-testid={`tech-edit-log-btn-${s.id}`} onClick={() => onEdit(s)} disabled={s.status === "cancelled"} className="self-start rounded-full bg-brand hover:bg-brand-dark text-white font-semibold shadow-btn">
        <ClipboardEdit className="w-4 h-4 mr-2" /> {s.case_log ? "Edit case log" : "Fill case log"}
      </Button>
    </div>
  );
}

export default function TechnicianDashboard() {
  const { user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [editing, setEditing] = useState(null);
  const [pwOpen, setPwOpen] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/technician/sessions");
      setSessions(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoaded(true); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="min-h-screen">
      <Navbar />
      <div className="max-w-5xl mx-auto px-6 py-10">
        <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
          <div>
            <div className="chip-light mb-3">Technician portal</div>
            <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-white" data-testid="tech-dashboard-title">Hi {user?.name?.split(" ")[0]}, your sessions</h1>
          </div>
          <Button data-testid="tech-change-password-btn" variant="outline" onClick={() => setPwOpen(true)} className="rounded-full border border-slate-300 bg-white text-[#0A1628] font-semibold hover:bg-slate-100">
            <KeyRound className="w-4 h-4 mr-2" /> Change password
          </Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="tech-session-list">
          {sessions.map((s) => <SessionCard key={s.id} s={s} onEdit={setEditing} />)}
        </div>
        {loaded && sessions.length === 0 && (
          <div data-testid="tech-no-sessions" className="soft-card p-10 text-center text-slate-500">No sessions assigned to you yet.</div>
        )}
      </div>
      <CaseLogDialog session={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      <ChangePasswordDialog open={pwOpen} onClose={() => setPwOpen(false)} />
    </div>
  );
}
